import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// ─── Financial Health (Home → Financial health, owner-only) ───────────────
// CJ 2026-07-22, Phase 3. The "push of a button" pulse: operating profit and
// margin per café + company for Today / Week-to-date / Month-to-date, reusing
// the P&L engine (/api/admin/pnl → operating_profit). Until fixed costs are
// entered, operating profit = after-supplies (prime) margin.

const money0 = (v) => (v == null ? '—' : `${v < 0 ? '-$' : '$'}${Math.abs(Number(v)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const marginPct = (profit, sales) => (sales > 0 ? `${(profit / sales * 100).toFixed(1)}%` : '—');
const profitColor = (v) => (v == null ? '#9e9e9e' : v < 0 ? '#b91c1c' : '#1d5b4c');

function localYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function periods() {
  const now = new Date();
  const day = now.getDay();
  const mon = new Date(now); mon.setDate(now.getDate() + (day === 0 ? -6 : 1 - day));
  const mtd = new Date(now.getFullYear(), now.getMonth(), 1);
  const t = localYmd(now);
  return [['Today', t, t], ['Week to date', localYmd(mon), t], ['Month to date', localYmd(mtd), t]];
}

export function FinancialHealthView({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const P = periods();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const out = {};
      for (const [name, f, t] of P) {
        try { out[name] = await api('GET', `/api/admin/pnl?from=${f}&to=${t}`, null, headers); }
        catch { out[name] = null; }
      }
      if (!cancelled) { setData(out); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cafes = (data['Month to date']?.pnl || data['Week to date']?.pnl || data['Today']?.pnl || [])
    .map((r) => ({ id: r.location_id, name: r.location_name }));
  const profitOf = (r) => (r.operating_profit != null ? r.operating_profit : r.after_supplies_margin);
  const cell = (periodName, locId) => {
    const d = data[periodName]; if (!d) return null;
    const r = d.pnl.find((x) => x.location_id === locId); if (!r) return null;
    return { profit: profitOf(r), sales: r.net_sales || 0 };
  };
  const company = (periodName) => {
    const d = data[periodName]; if (!d) return null;
    return {
      profit: d.pnl.reduce((s, r) => s + (profitOf(r) ?? 0), 0),
      sales: d.pnl.reduce((s, r) => s + (r.net_sales || 0), 0),
    };
  };
  const anyOverhead = Object.values(data).some((d) => d?.pnl?.some((r) => (r.total_overhead || 0) > 0));

  const mtd = company('Month to date');
  const th = { padding: '6px 10px', textAlign: 'right', color: '#757575', fontSize: 12, fontWeight: 600 };
  const td = { padding: '5px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  const ProfitCell = ({ c }) => {
    if (!c) return <td style={td}>—</td>;
    return (
      <td style={td}>
        <div style={{ fontWeight: 600, color: profitColor(c.profit) }}>{money0(c.profit)}</div>
        <div style={{ fontSize: 11, color: '#9e9e9e' }}>{marginPct(c.profit, c.sales)}</div>
      </td>
    );
  };

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="text-base font-semibold">Financial health</div>
        <div className="text-xs text-gray-500">
          {anyOverhead ? 'Operating profit' : 'Prime margin'} &amp; margin per café — Today, week-to-date, month-to-date.
          {!anyOverhead && ' (Enter fixed costs in Settings → Fixed costs to turn this into true operating profit.)'}
        </div>
      </div>

      {mtd && (
        <div className="card" style={{ borderLeft: `4px solid ${profitColor(mtd.profit)}` }}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Company · month to date</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: profitColor(mtd.profit) }}>{money0(mtd.profit)}</div>
            <div className="text-sm text-gray-500">{marginPct(mtd.profit, mtd.sales)} of {money0(mtd.sales)} sales · {anyOverhead ? 'operating profit' : 'prime margin'}</div>
          </div>
        </div>
      )}

      <div className="card">
        {loading ? <div className="text-sm text-gray-500">Loading…</div>
          : cafes.length === 0 ? <div className="text-sm text-gray-500">No data yet.</div>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <th style={{ ...th, textAlign: 'left' }}>{anyOverhead ? 'Operating profit' : 'Prime margin'}</th>
                      {P.map(([name]) => <th key={name} style={th}>{name}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {cafes.map((cf) => (
                      <tr key={cf.id} style={{ borderBottom: '1px solid #f2f2f2' }}>
                        <td style={{ padding: '5px 10px', fontWeight: 500 }}>{cf.name}</td>
                        {P.map(([name]) => <ProfitCell key={name} c={cell(name, cf.id)} />)}
                      </tr>
                    ))}
                    <tr style={{ borderTop: '2px solid #d0d4d9', background: '#faf8f4' }}>
                      <td style={{ padding: '6px 10px', fontWeight: 700 }}>Company</td>
                      {P.map(([name]) => <ProfitCell key={name} c={company(name)} />)}
                    </tr>
                  </tbody>
                </table>
                <div className="text-xs text-gray-400 mt-3" style={{ lineHeight: 1.4 }}>
                  Margin % is {anyOverhead ? 'operating profit' : 'prime margin'} ÷ net sales. Labor + employer tax are estimates; COGS is theoretical (costed items only); fixed overhead is a config run-rate. Today &amp; short periods are live estimates — the certified weekly payroll run is the exact labor figure.
                  {data['Month to date']?.as_of && <> Data as of {String(data['Month to date'].as_of).slice(0, 16).replace('T', ' ')}.</>}
                </div>
              </div>
            )}
      </div>
    </div>
  );
}
